import {makeApiCall} from '../core';
import type {
  AdminNotificationPreferenceKey,
  ItemEnvelope,
  NotificationType,
} from '@ruta/shared';

export interface UserProfile {
  cognitoSub: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  fullName?: string;
  createdAt?: string;
  updatedAt?: string;
  // Extended profile fields from OrganizationMember
  languagePreference?: 'en' | 'es';
  /** Measure and price time fields in minutes or hours (RUT-4434). Absent = minutes. */
  timeUnitPreference?: 'min' | 'hr';
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  profilePictureUrl?: string;
  memberId?: string; // For avatar uploads
  /** The caller's role in this org (RUT-4122). */
  roleId?: string;
}

/**
 * Channel settings for a specific notification type
 */
export interface NotificationChannelSettings {
  email: boolean;
  sms: boolean;
  inApp: boolean;
}

/**
 * Admin notification preferences — per-type channel settings, with legacy
 * category keys kept so previously saved grouped switches still apply.
 */
export type AdminNotificationPreferences = Partial<
  Record<AdminNotificationPreferenceKey | NotificationType, NotificationChannelSettings>
>;

export interface NotificationPreferences {
  emailNotifications?: {
    jobAssignments: boolean;
    jobUpdates: boolean;
    scheduleChanges: boolean;
    paymentNotifications: boolean;
    systemUpdates: boolean;
  };
  smsNotifications?: {
    jobAssignments: boolean;
    urgentUpdates: boolean;
    scheduleReminders: boolean;
  };
  pushNotifications?: {
    enabled: boolean;
    jobAssignments: boolean;
    jobUpdates: boolean;
    messages: boolean;
  };
  /** Admin-specific notification preferences (per-type granular control) */
  adminPreferences?: AdminNotificationPreferences;
  /** Types this user has hidden from the office bell (RUT-3531). */
  hiddenInAppTypes?: NotificationType[];
}

export interface UserProfileResponse {
  profile: UserProfile;
  notificationPreferences: NotificationPreferences;
}

export interface UpdateUserProfileRequest {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
  // Extended profile fields
  languagePreference?: 'en' | 'es';
  /** Measure and price time fields in minutes or hours (RUT-4434). Absent = minutes. */
  timeUnitPreference?: 'min' | 'hr';
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  profilePictureUrl?: string | null;
  /** Replaces the one-time signup password after email confirm (RUT-4518). */
  newPassword?: string;
}

export async function getUserProfile(): Promise<UserProfileResponse> {
  const response = await makeApiCall<ItemEnvelope<UserProfileResponse>>(
    'GET',
    '/users/profile',
  );
  return response.data;
}

export async function updateUserProfile(data: UpdateUserProfileRequest): Promise<void> {
  await makeApiCall<void>('PATCH', '/users/profile', data);
}

export async function updateNotificationPreferences(preferences: NotificationPreferences): Promise<void> {
  await makeApiCall<void>('PUT', '/users/notification-preferences', preferences);
}
